import { Archive, RefreshCw } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useOrganization } from "@/client/backend/organization";
import {
  iosSnapshotPageSize,
  useOrganizationIOSSnapshot,
  useOrganizationIOSSnapshots,
  type IOSSnapshot,
} from "@/client/backend/iosSnapshots";
import { formatArchiveSize } from "@/client/backend/xcodeSnapshots";
import { CopyButton } from "@/components/copy";
import { AppShell, EmptyState, PageHeader } from "@/components/layout/AppShell";
import { SnapshotDeleteButton } from "@/components/snapshot-delete-button";
import { SnapshotTabs } from "@/components/snapshot-tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatRelativeTime } from "@/lib/format";

function SavedAt({ snapshot }: { snapshot: IOSSnapshot }) {
  if (!snapshot.saved) return <span className="text-muted-foreground">Not saved yet</span>;
  if (!snapshot.publishedAt) return <span className="text-muted-foreground">Saved</span>;
  return (
    <time dateTime={snapshot.publishedAt} title={new Date(snapshot.publishedAt).toLocaleString()}>
      {formatRelativeTime(snapshot.publishedAt)}
    </time>
  );
}

function SnapshotList({ organizationId }: { organizationId?: string }) {
  const [prefix, setPrefix] = useState("");
  const [offset, setOffset] = useState(0);
  const { data, error, isValidating, mutate } = useOrganizationIOSSnapshots(organizationId, prefix, offset);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label="Filter simulator snapshots by key prefix"
          placeholder="Filter by key prefix..."
          value={prefix}
          onChange={(event) => {
            setPrefix(event.target.value.trim());
            setOffset(0);
          }}
          className="w-full sm:max-w-xs"
        />
        <Button variant="outline" size="sm" onClick={() => void mutate()} disabled={isValidating}>
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-sm text-danger">
          Failed to load simulator snapshots: {error.message}
        </p>
      )}
      {!data ? (
        !error && (
          <p role="status" className="text-sm text-muted-foreground">
            Loading simulator snapshots...
          </p>
        )
      ) : data.snapshots.length === 0 ? (
        <EmptyState
          icon={<Archive />}
          title={prefix || offset ? "No matching snapshots" : "No simulator snapshots yet"}
          description={
            prefix || offset
              ? "Try another key prefix or return to the previous page."
              : "Add snapshot-key to a Preview link, then save the device state. The key and its saved archive will appear here."
          }
        />
      ) : (
        <Table aria-label="iOS simulator snapshots" className="min-w-[44rem]">
          <TableHeader>
            <TableRow>
              <TableHead>Key</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Last saved</TableHead>
              <TableHead className="hidden sm:table-cell">iOS</TableHead>
              <TableHead className="hidden lg:table-cell">Last used</TableHead>
              <TableHead>
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.snapshots.map((snapshot) => (
              <TableRow key={snapshot.key}>
                <TableCell className="max-w-80">
                  <Link
                    to={`/snapshots/${encodeURIComponent(snapshot.key)}?type=ios`}
                    className="block truncate font-semibold hover:underline"
                    title={snapshot.key}
                  >
                    {snapshot.key}
                  </Link>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <span className={snapshot.saved ? "text-success" : "text-muted-foreground"}>
                    {snapshot.saved ? "Saved" : "Not saved yet"}
                  </span>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {snapshot.archiveSizeBytes === undefined ? "—" : formatArchiveSize(snapshot.archiveSizeBytes)}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <SavedAt snapshot={snapshot} />
                </TableCell>
                <TableCell className="hidden sm:table-cell">{snapshot.iosVersion ?? "—"}</TableCell>
                <TableCell className="hidden whitespace-nowrap lg:table-cell">
                  <time dateTime={snapshot.lastUsedAt} title={new Date(snapshot.lastUsedAt).toLocaleString()}>
                    {formatRelativeTime(snapshot.lastUsedAt)}
                  </time>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <CopyButton value={snapshot.key} label={`Copy key ${snapshot.key}`} />
                    {organizationId && (
                      <SnapshotDeleteButton kind="ios" organizationId={organizationId} snapshotKey={snapshot.key} onDeleted={() => void mutate()} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {data && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {data.snapshots.length
              ? `Showing keys ${offset + 1}–${offset + data.snapshots.length}, sorted by key.`
              : "No keys on this page."}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - iosSnapshotPageSize))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={data.nextOffset === undefined}
              onClick={() => {
                if (data.nextOffset !== undefined) setOffset(data.nextOffset);
              }}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SnapshotDetail({ organizationId, snapshotKey }: { organizationId?: string; snapshotKey: string }) {
  const navigate = useNavigate();
  const { data: snapshot, error, isValidating, mutate } = useOrganizationIOSSnapshot(organizationId, snapshotKey);
  if (error && !snapshot)
    return (
      <div className="flex flex-col items-start gap-3">
        <p role="alert" className="text-sm text-danger">
          Failed to load simulator snapshot: {error.message}
        </p>
        <Button variant="outline" onClick={() => void mutate()}>
          Retry
        </Button>
      </div>
    );
  if (snapshot === undefined)
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading simulator snapshot...
      </p>
    );
  if (snapshot === null)
    return (
      <EmptyState
        icon={<Archive />}
        title="No simulator snapshot under this key"
        description="This key has not been used, or it was deleted."
      />
    );
  const previewParams = new URLSearchParams({ platform: "ios", "snapshot-key": snapshot.key });
  if (organizationId) previewParams.set("organization", organizationId);
  if (snapshot.iosVersion) previewParams.set("osVersion", snapshot.iosVersion);
  const previewPath = `/stream?${previewParams}`;
  const previewUrl = new URL(previewPath, window.location.origin).href;
  return (
    <div className="flex max-w-4xl flex-col gap-6">
      {error && (
        <p role="alert" className="text-sm text-danger">
          Could not refresh this snapshot. Showing the last loaded information: {error.message}
        </p>
      )}
      {organizationId && (
        <div className="self-end">
          <SnapshotDeleteButton kind="ios" organizationId={organizationId} snapshotKey={snapshotKey} onDeleted={() => navigate("/snapshots?type=ios")} />
        </div>
      )}
      <Card variant="section">
        <CardHeader>
          <CardTitle>Saved device state</CardTitle>
          <CardDescription>
            Installed apps, their data, keychain, and supported permissions. Each save replaces the archive under this
            key.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-4 gap-y-3 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]">
            <dt className="text-muted-foreground">Key</dt>
            <dd className="flex min-w-0 items-center gap-1">
              <span className="break-all font-semibold">{snapshot.key}</span>
              <CopyButton value={snapshot.key} label="Copy snapshot key" />
            </dd>
            <dt className="text-muted-foreground">Status</dt>
            <dd>{snapshot.saved ? "Saved" : "Not saved yet"}</dd>
            <dt className="text-muted-foreground">Size</dt>
            <dd>
              {snapshot.archiveSizeBytes === undefined
                ? "No saved archive"
                : `${formatArchiveSize(snapshot.archiveSizeBytes)} compressed`}
            </dd>
            <dt className="text-muted-foreground">Last saved</dt>
            <dd>
              {snapshot.publishedAt ? new Date(snapshot.publishedAt).toLocaleString() : <SavedAt snapshot={snapshot} />}
            </dd>
            <dt className="text-muted-foreground">iOS version</dt>
            <dd>{snapshot.iosVersion ?? "Not recorded"}</dd>
            <dt className="text-muted-foreground">Key created</dt>
            <dd>{new Date(snapshot.createdAt).toLocaleString()}</dd>
            <dt className="text-muted-foreground">Last used</dt>
            <dd>
              {new Date(snapshot.lastUsedAt).toLocaleString()}
              <p className="mt-1 text-xs text-muted-foreground">When a simulator last started with this key.</p>
            </dd>
          </dl>
          <Button className="mt-4" variant="outline" size="sm" onClick={() => void mutate()} disabled={isValidating}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </CardContent>
      </Card>
      <Card variant="section">
        <CardHeader>
          <CardTitle>Use this snapshot</CardTitle>
          <CardDescription>
            Open a fresh simulator with the same key. Add an app asset to the Preview link to install a newer build
            after restoration.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="flex items-start gap-2 rounded-md border p-3">
            <code className="min-w-0 flex-1 break-all text-xs">{previewUrl}</code>
            <CopyButton value={previewUrl} label="Copy Preview link" />
          </div>
          <Button variant="outline" className="self-start" asChild>
            <Link to={previewPath}>Open Preview</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export function IOSSnapshotsPage() {
  const { key } = useParams<{ key?: string }>();
  const [organization] = useOrganization();
  const organizationId = organization === "loading" ? undefined : organization.tid;
  return (
    <AppShell title={key ?? "Snapshots"}>
      <div className="flex flex-col gap-6">
        {key && (
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
            <Link to="/snapshots?type=ios" className="hover:underline">
              iOS simulator snapshots
            </Link>
            <span aria-hidden="true" className="mx-1">
              /
            </span>
            <span className="break-all">{key}</span>
          </nav>
        )}
        <PageHeader
          title={key ?? "Snapshots"}
          description={
            key
              ? "iOS simulator snapshot"
              : "Saved simulator apps and data for your organization. Reuse a key to restore its latest saved state."
          }
        />
        <SnapshotTabs ios />
        {key ? (
          <SnapshotDetail key={`${organizationId}:${key}`} organizationId={organizationId} snapshotKey={key} />
        ) : (
          <SnapshotList key={organizationId} organizationId={organizationId} />
        )}
      </div>
    </AppShell>
  );
}
